import { useCallback, useRef } from "react";
import type { createApi } from "./api";
import type { UiSnapshot } from "./voiceDispatch";

type VoiceApi = ReturnType<typeof createApi>;

type Deps = {
  api: VoiceApi;
  getUiContext: () => UiSnapshot;
  applyCommands: (raw: unknown) => void | Promise<void>;
  onApplied?: (commandCount: number) => void;
  onError?: (message: string) => void;
  sessionId?: string | null;
};

const DEDUPE_MS = 30_000;

/** Normalize backend readback for TTS (strip accidental trailing Done from LLM). */
export function normalizeReadback(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return "";
  }
  const withoutDone = trimmed.replace(/(?:\s+)?\bDone\.?\s*$/i, "").trim();
  if (!withoutDone) {
    return "";
  }
  if (/[.!?]$/.test(withoutDone)) {
    return withoutDone;
  }
  return `${withoutDone}.`;
}

async function runVoiceCommandOnce(getDeps: () => Deps, trimmed: string): Promise<string> {
  const deps = getDeps();
  try {
    const plan = await deps.api.voiceCommand(trimmed, deps.getUiContext(), deps.sessionId);
    const commands = plan.commands ?? [];
    await Promise.resolve(deps.applyCommands(commands));
    deps.onApplied?.(commands.length);
    return normalizeReadback(plan.response);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Voice command failed";
    deps.onError?.(message);
    return message;
  }
}

/** Shared runner for query_agent (testable without React). */
export function createVoiceCommandRunner(getDeps: () => Deps) {
  let cache: { key: string; readback: string; at: number } | null = null;
  let inflightByKey: { key: string; promise: Promise<string> } | null = null;
  let queueTail: Promise<unknown> = Promise.resolve();

  return async (query: string): Promise<string> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return "";
    }

    const key = trimmed.toLowerCase();
    if (cache && cache.key === key && Date.now() - cache.at < DEDUPE_MS) {
      return cache.readback;
    }

    if (inflightByKey?.key === key) {
      return inflightByKey.promise;
    }

    const run = async (): Promise<string> => {
      const readback = await runVoiceCommandOnce(getDeps, trimmed);
      cache = { key, readback, at: Date.now() };
      return readback;
    };

    const promise = queueTail.then(run, run);
    queueTail = promise.then(
      () => undefined,
      () => undefined,
    );
    inflightByKey = { key, promise };

    try {
      return await promise;
    } finally {
      if (inflightByKey?.promise === promise) {
        inflightByKey = null;
      }
    }
  };
}

export function useVoiceCommand(deps: Deps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const runnerRef = useRef<ReturnType<typeof createVoiceCommandRunner>>();
  if (!runnerRef.current) {
    runnerRef.current = createVoiceCommandRunner(() => depsRef.current);
  }

  return useCallback((query: string) => runnerRef.current!(query), []);
}

export function lastUserTranscriptEntry(
  transcript: ReadonlyArray<{ role: string; text: string; timestamp: number }>,
) {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const entry = transcript[i];
    if (entry?.role === "user" && entry.text.trim()) {
      return entry;
    }
  }
  return null;
}
